import type {
  ApplicationAdapterLike,
  IS3VdrClient,
  IUploadFile,
  UploadProgressCallback,
  VdrCredentialPayload,
} from './types';
import type StoreService from 'scaleflow/services/store';
import type { AwsCredentialIdentity } from '@smithy/types';
import { HOST } from 'scaleflow/adapters/application';
import {
  AbortMultipartUploadCommand,
  CompleteMultipartUploadCommand,
  CopyObjectCommand,
  CreateMultipartUploadCommand,
  DeleteObjectCommand,
  GetObjectCommand,
  HeadObjectCommand,
  ListObjectsV2Command,
  type ListObjectsV2CommandOutput,
  PutObjectCommand,
  type PutObjectCommandOutput,
  S3Client,
  UploadPartCopyCommand,
} from '@aws-sdk/client-s3';
import { Upload } from '@aws-sdk/lib-storage';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';
import ENV from 'scaleflow/config/environment';
import {
  requiresCredentials,
  requiresCredentialsStream,
} from './with-credentials';

/** One object as the app cares about it, flattened out of the S3 response. */
export type S3ObjectSummary = {
  key: string;
  name: string;
  size: number;
  lastModified: Date | null;
};

/**
 * Whether logs may carry diagnostic detail.
 *
 * The browser console is readable by whoever is sitting at the page, so a
 * message naming our storage layout, the prefix a credential is scoped to, or
 * the IAM action a policy is missing hands a prober a map of the system for
 * free. Every log below says what happened; only a local developer also learns
 * how we are built.
 *
 * Allow-listed to the two environments no customer reaches, rather than written
 * as `!== 'production'` — staging serves real users, so "not production" is not
 * the same question as "safe to disclose in".
 *
 * Thrown `Error` messages are deliberately NOT keyed on this: a message that
 * changes between environments cannot be asserted on, and those are already
 * written to name a cause without naming a key or a policy.
 */
const LOG_DETAIL =
  ENV.environment === 'development' || ENV.environment === 'test';

/** Diagnostic suffix outside production; nothing in it. */
function detail(text: string): string {
  return LOG_DETAIL ? ` ${text}` : '';
}

const MIB = 1024 * 1024;

/**
 * S3's limits for copying, all fixed by the service:
 *
 * - a single-request `CopyObject` tops out at 5 GiB, above which the copy has to
 *   be composed from `UploadPartCopy` calls;
 * - every part except the last must be at least 5 MiB, and no part may exceed
 *   5 GiB;
 * - a multipart upload may have at most 10,000 parts.
 */
const SINGLE_REQUEST_COPY_LIMIT_BYTES = 5 * 1024 * MIB;
const MIN_PART_BYTES = 5 * MIB;
const MAX_PART_BYTES = 5 * 1024 * MIB;
const MAX_PARTS = 10_000;

/**
 * Starting part size for a multipart copy. Larger parts mean fewer requests;
 * `copyPartSizeFor` grows this when an object is too big to fit in
 * `MAX_PARTS` at this size.
 */
const DEFAULT_COPY_PART_BYTES = 512 * MIB;

/**
 * How many `UploadPartCopy` requests are in flight at once.
 *
 * These are server-side copies, so the browser only pays for the request, not
 * the bytes — but this still runs in a page alongside whatever else the app is
 * doing, so it is deliberately modest rather than maximal.
 */
const COPY_CONCURRENCY = 8;

/** One part of a multipart copy, as an inclusive byte range. */
export type CopyPart = { partNumber: number; start: number; end: number };

/**
 * Part size for an object of `size` bytes.
 *
 * Grows past the default only when it has to: an object big enough to need more
 * than 10,000 default-sized parts gets a part size that brings it back under
 * the limit.
 */
export function copyPartSizeFor(size: number): number {
  let partSize = DEFAULT_COPY_PART_BYTES;
  if (Math.ceil(size / partSize) > MAX_PARTS) {
    partSize = Math.ceil(size / MAX_PARTS);
  }
  return Math.min(Math.max(partSize, MIN_PART_BYTES), MAX_PART_BYTES);
}

/**
 * Splits an object into inclusive byte ranges, one per part.
 *
 * The final part is whatever is left, and is the only one allowed to fall below
 * the 5 MiB minimum.
 */
export function copyPartRanges(
  size: number,
  partSize = copyPartSizeFor(size),
): CopyPart[] {
  const parts: CopyPart[] = [];
  for (let start = 0; start < size; start += partSize) {
    parts.push({
      partNumber: parts.length + 1,
      start,
      end: Math.min(start + partSize, size) - 1,
    });
  }
  return parts;
}

/**
 * Runs `task` over `items` with at most `limit` in flight, preserving input
 * order in the results.
 *
 * A plain `Promise.all` over 10,000 parts would open 10,000 requests at once.
 */
export async function mapWithConcurrency<T, R>(
  items: T[],
  limit: number,
  task: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  const results = new Array<R>(items.length);
  let next = 0;

  const worker = async () => {
    while (next < items.length) {
      const index = next++;
      results[index] = await task(items[index]!, index);
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, worker),
  );
  return results;
}

export let s3VdrClient: S3VDRClient | null = null;

/** Endpoint that mints the scoped, short-lived credentials this client signs with. */
export const VDR_CREDENTIALS_PATH = '/credentials/vdr';

/**
 * Exported for tests only — application code should go through
 * `initializeVdrClient`, which owns the singleton and its credential
 * lifecycle. Tests need to construct an instance with stubbed internals
 * rather than let `initializeClient` reach the credentials endpoint.
 */
export class S3VDRClient {
  // Extracted configs to readonly properties - consider externalizing these in future
  private readonly REGION = ENV.aws.region;

  /**
   * Not `readonly`: the credentials endpoint states which bucket it scoped the
   * credentials to, and that answer wins. The ENV value is only the fallback
   * for the window before the first fetch resolves — in practice never used,
   * because every public method is guarded by `@requiresCredentials`, which
   * fetches before any command is built.
   */
  private bucket = ENV.storage.AWSS3.vdr_bucket;

  /** Key prefix the credentials are scoped to, as reported by the server. */
  private credentialPrefix = '';

  private s3Client: S3Client | null = null;
  private store: StoreService;
  private credentials: AwsCredentialIdentity | null = null;
  private credentialExpiration: Date | null = null;
  private pendingRefresh: Promise<void> | null = null;

  constructor({ store }: IS3VdrClient) {
    if (!store) {
      throw new Error('store needed to instantiate S3VDRClient');
    }
    this.store = store;
  }

  /** The bucket the current credentials are scoped to. */
  public get vdrBucket(): string {
    return this.bucket;
  }

  /**
   * The key prefix the credentials are scoped to.
   *
   * Reported, never applied: callers already build fully qualified,
   * tenant-rooted keys (`app/utils/manual-repo-archives.ts`), so prepending it
   * here would produce `tenant-1/tenant-1/…`.
   */
  public get prefix(): string {
    return this.credentialPrefix;
  }

  /**
   * Fetches credentials from the API.
   *
   * Goes through the application adapter rather than a bare `fetch` so the
   * request carries `Authorization` and `Tenant-Id`, and so a 401 runs the
   * adapter's refresh-and-retry path instead of failing outright.
   */
  private async fetchCredentials(): Promise<void> {
    const adapter = this.store.adapterFor(
      'application',
    ) as unknown as ApplicationAdapterLike;

    const payload = (await adapter.ajax(
      `${HOST}${VDR_CREDENTIALS_PATH}`,
      'POST',
    )) as VdrCredentialPayload;

    const accessKeyId = payload?.data?.id;
    const attributes = payload?.data?.attributes;

    if (
      !accessKeyId ||
      !attributes?.secret_access_key ||
      !attributes?.session_token ||
      !attributes?.expiration
    ) {
      throw new Error(
        'The credentials service returned an incomplete response.',
      );
    }

    const expiration = new Date(attributes.expiration);
    // An unparseable date yields `Invalid Date`, whose `getTime()` is `NaN` —
    // and `NaN < EXPIRATION_MS` is `false`, so the AWS SDK would read the
    // credentials as permanently fresh and never ask for another set. Failing
    // loudly here is the only way that mistake stays visible.
    if (Number.isNaN(expiration.getTime())) {
      throw new Error('The credentials service returned an unreadable expiry.');
    }

    this.credentials = {
      accessKeyId,
      secretAccessKey: attributes.secret_access_key,
      sessionToken: attributes.session_token,
      // Must be a `Date`. The SDK's expiry check calls `.getTime()` on it, so a
      // raw ISO string throws on the *second* signed request, not the first.
      expiration,
    };
    this.credentialExpiration = expiration;
    if (attributes.bucket) this.bucket = attributes.bucket;
    this.credentialPrefix = attributes.prefix ?? '';
  }

  /**
   * Coalesced so that concurrent callers share one request. The multipart copy
   * path alone can have eight commands in flight, and without this each would
   * open its own credential fetch.
   */
  private async refreshCredentials(): Promise<void> {
    this.pendingRefresh ??= this.fetchCredentials().finally(() => {
      this.pendingRefresh = null;
    });
    return this.pendingRefresh;
  }

  /**
   * The five-minute window must stay greater than or equal to the AWS SDK's own
   * `EXPIRATION_MS` (300_000, in
   * `@smithy/core/…/util-identity-and-auth/memoizeIdentityProvider.js`). If it
   * were shorter, the SDK would decide a refresh is due, `credentialProvider`
   * would decline to fetch, the memoizer would cache the same stale value and
   * re-ask on the very next request — a per-request no-op loop.
   */
  private isCredentialExpiringSoon(): boolean {
    if (!this.credentialExpiration) return true;
    const FIVE_MINUTES_MS = 5 * 60 * 1000;
    return this.credentialExpiration.getTime() - Date.now() <= FIVE_MINUTES_MS;
  }

  /**
   * `protected` rather than `private` because its only caller is now the
   * `@requiresCredentials` decorator, which reaches it structurally from
   * outside the class body. Still not part of the public surface.
   */
  protected async ensureCredentialsValid(): Promise<void> {
    if (this.isCredentialExpiringSoon()) {
      if (LOG_DETAIL) {
        console.debug('S3VDRClient credentials expiring soon, refreshing...');
      }
      await this.refreshCredentials();
    }
    // Idempotent via `??=` — safe to call every time, and load-bearing the
    // first time. If the very first fetch (from `initializeClient`) throws,
    // this line is never reached and `s3Client` stays null, which is correct.
    // But `ensureCredentialsValid` also runs on every later call through
    // `credentialProvider`, so a client that failed its first fetch still
    // gets its `s3Client` built here as soon as a subsequent refresh
    // succeeds — without this, a client stuck with `s3Client === null` after
    // one failed boot-time fetch would stay broken forever, since nothing
    // else ever calls `ensureS3Client` again.
    this.ensureS3Client();
  }

  /**
   * Handed to the `S3Client` as its credential source rather than a fixed
   * credential object, which is what makes refresh work mid-transfer.
   *
   * `@aws-sdk/core`'s `resolveAwsSdkSigV4Config` wraps any provider in
   * `memoizeIdentityProvider`, and the signing middleware resolves it once per
   * `send()`. Since `@aws-sdk/lib-storage`'s `Upload` issues one `send()` per
   * part, a multi-hour upload re-checks expiry on every part and refreshes
   * without interrupting the transfer — something a per-method guard, which
   * runs once at method entry, cannot do.
   *
   * An arrow-function property, not a method: the SDK stores and invokes it
   * unbound.
   */
  private credentialProvider = async (): Promise<AwsCredentialIdentity> => {
    await this.ensureCredentialsValid();
    if (!this.credentials) {
      throw new Error('Storage credentials are unavailable.');
    }
    return this.credentials;
  };

  /**
   * The current credentials, refreshed first if they are near expiry.
   *
   * Exists for the embedded VDR app, which has no credentials of its own and
   * asks this client for them over `postMessage`. Deliberately a thin wrapper
   * on `credentialProvider` so the iframe and the S3 signer can never diverge
   * on caching or refresh behaviour.
   */
  public async getCredentials(): Promise<AwsCredentialIdentity> {
    return this.credentialProvider();
  }

  /**
   * Built at most once per client. Recreating it on refresh — as the Cognito
   * implementation did — would strand the parts of any transfer already in
   * flight on a client nothing else references.
   */
  private ensureS3Client(): void {
    this.s3Client ??= new S3Client({
      region: this.REGION,
      // Defaults to 'WHEN_SUPPORTED', which auto-attaches a CRC32 checksum to
      // CreateMultipartUpload — but @aws-sdk/lib-storage's per-part
      // UploadPart calls for browser body types (Blob/Uint8Array chunks)
      // don't reliably attach the matching per-part checksum, so
      // CompleteMultipartUpload then fails with "It was missing for part N in
      // the request." S3 doesn't require a checksum for these operations, so
      // skipping it entirely avoids the mismatch.
      requestChecksumCalculation: 'WHEN_REQUIRED',
      credentials: this.credentialProvider,
    });
  }

  /**
   * Initializes the S3 client with temporary credentials.
   */
  public async initializeClient(): Promise<void> {
    if (this.s3Client && this.credentials) {
      if (LOG_DETAIL) console.debug('S3VDRClient already initialized.');
      return;
    }
    await this.ensureCredentialsValid();
    this.ensureS3Client();
    if (LOG_DETAIL) console.debug('VDR client initialized');
  }

  public updateStore(store: StoreService): void {
    this.store = store;
  }

  /**
   * Warns when a key falls outside the prefix the credentials are scoped to.
   *
   * The failure it pre-empts is a bare `AccessDenied` from S3 with nothing to
   * say which of the key, the policy, or the credentials was wrong. Silent in
   * production, and never throws: the server's prefix is advisory, and a false
   * positive must not break an upload.
   */
  private assertKeyInPrefix(key: string): void {
    if (!LOG_DETAIL) return;
    if (!this.credentialPrefix || key.startsWith(this.credentialPrefix)) return;
    console.warn(
      `S3VDRClient: key "${key}" is outside the credentials' prefix "${this.credentialPrefix}" — S3 will likely reject it.`,
    );
  }

  /**
   * Checks if a file exists in the S3 bucket.
   * @param fileName The key name of the file.
   * @returns Promise resolving to true if file exists, else false.
   */
  @requiresCredentials
  public async fileExists(fileName: string): Promise<boolean> {
    const command = new HeadObjectCommand({
      Bucket: this.bucket,
      Key: fileName,
    });

    try {
      await this.s3Client!.send(command);
      return true; // If no error, file exists
    } catch (error: unknown) {
      const err = error as {
        name?: string;
        $metadata?: { httpStatusCode?: number };
      };
      const status = err.$metadata?.httpStatusCode;

      // Classified on the status code first, with the name only as a fallback.
      // `HeadObject` returns no response body, so there is no error code for
      // the SDK to parse: recent versions surface a bodiless failure as
      // `Unknown`/`UnknownError` rather than `NotFound`, and keying off the
      // name alone let a plain miss escape as an unhandled error.
      if (status === 404 || err.name === 'NotFound') return false;

      // 403, not 404, is what S3 returns for a key the caller cannot see —
      // without `s3:ListBucket` on the bucket it must not reveal whether the
      // object exists. These credentials are scoped to a tenant prefix, so 403
      // is the normal answer both for a key that is missing and for one outside
      // the grant. Either way it is not available to this user, which is what
      // the caller asked. Warned rather than silent, because a policy that is
      // simply wrong produces the identical 403 — if files that *do* exist also
      // report absent, that is the tell.
      if (
        status === 403 ||
        err.name === 'AccessDenied' ||
        err.name === 'Forbidden'
      ) {
        console.warn(
          `That file is not available.${detail(
            `HeadObject on "${fileName}" returned 403; the grant covers "${this.credentialPrefix}". If keys inside that prefix also report absent, the policy likely lacks s3:ListBucket.`,
          )}`,
        );
        return false;
      }

      // The error object itself is withheld in production: it carries the
      // bucket, the signed url and the request id. Sentry still receives the
      // exception from the route's error handler, so nothing is lost.
      if (LOG_DETAIL) {
        console.error(
          `Unable to verify "${fileName}" (status ${status ?? 'unknown'}):`,
          error,
        );
      } else {
        console.error('Could not check whether that file is available.');
      }
      throw error;
    }
  }

  /**
   * Uploads a file to the S3 bucket.
   */
  @requiresCredentials
  public async uploadFileToBucket({
    key,
    body,
    contentType,
  }: IUploadFile): Promise<PutObjectCommandOutput> {
    this.assertKeyInPrefix(key);
    const command = new PutObjectCommand({
      Bucket: this.bucket,
      Key: key,
      Body: body,
      ContentType: contentType,
    });

    return await this.s3Client!.send(command);
  }

  /**
   * Uploads a file to the S3 bucket using multipart upload, for archives that
   * can run several GB — handles part-sizing, per-part retry, and progress.
   */
  @requiresCredentials
  public async uploadFileMultipart(
    { key, body, contentType }: IUploadFile,
    onProgress?: UploadProgressCallback,
  ): Promise<void> {
    this.assertKeyInPrefix(key);
    const upload = new Upload({
      client: this.s3Client!,
      params: {
        Bucket: this.bucket,
        Key: key,
        Body: body,
        ContentType: contentType,
      },
    });

    if (onProgress) {
      upload.on('httpUploadProgress', (progress) => {
        onProgress(progress.loaded ?? 0, progress.total);
      });
    }

    await upload.done();
  }

  /**
   * Lists every object under a prefix.
   *
   * Pages through the results rather than taking the first response: S3 caps
   * ListObjectsV2 at 1000 keys and signals more with IsTruncated, so a single
   * call would silently hide a tenant's 1001st archive.
   */
  @requiresCredentials
  public async listObjects(prefix: string): Promise<S3ObjectSummary[]> {
    // Captured once: `this.bucket` can be reassigned by a credential refresh
    // mid-pagination, and every page of this listing must come from the same
    // bucket the first page did.
    const bucket = this.bucket;
    const objects: S3ObjectSummary[] = [];
    let continuationToken: string | undefined = undefined;

    do {
      const response: ListObjectsV2CommandOutput = await this.s3Client!.send(
        new ListObjectsV2Command({
          Bucket: bucket,
          Prefix: prefix,
          ContinuationToken: continuationToken,
        }),
      );

      for (const object of response.Contents ?? []) {
        const key = object.Key ?? '';
        // A "directory" placeholder — a zero-byte object whose key is the
        // prefix itself — is not a file the user uploaded, so skip it.
        if (!key || key.endsWith('/')) continue;

        objects.push({
          key,
          name: key.split('/').pop() ?? key,
          size: object.Size ?? 0,
          lastModified: object.LastModified ?? null,
        });
      }

      continuationToken = response.IsTruncated
        ? response.NextContinuationToken
        : undefined;
    } while (continuationToken);

    return objects;
  }

  /**
   * Deletes a single object. S3 treats deleting a key that is not there as a
   * success, so this is idempotent and needs no existence check first.
   */
  @requiresCredentials
  public async deleteObject(key: string): Promise<void> {
    await this.s3Client!.send(
      new DeleteObjectCommand({
        Bucket: this.bucket,
        Key: key,
      }),
    );
  }

  /**
   * Server-side copy within this bucket — the bytes never travel through the
   * browser, which is what makes copying a multi-GB archive viable at all.
   *
   * Objects up to 5 GiB go in one `CopyObject` request. Above that S3 refuses,
   * so the copy is composed from `UploadPartCopy` calls instead. The caller sees
   * no difference either way.
   */
  @requiresCredentials
  public async copyObject(
    sourceKey: string,
    destinationKey: string,
  ): Promise<void> {
    // Captured once: a copy this large can run long enough for a credential
    // refresh to land mid-flight, and `this.bucket` would then point
    // `CompleteMultipartUpload`/`CopyObject` at a different bucket than the
    // one the read/`CreateMultipartUpload` used.
    const bucket = this.bucket;
    this.assertKeyInPrefix(destinationKey);
    const head = await this.s3Client!.send(
      new HeadObjectCommand({ Bucket: bucket, Key: sourceKey }),
    );
    const size = head.ContentLength ?? 0;

    if (size > SINGLE_REQUEST_COPY_LIMIT_BYTES) {
      await this.copyObjectMultipart(
        bucket,
        sourceKey,
        destinationKey,
        size,
        head.ContentType,
      );
      return;
    }

    await this.s3Client!.send(
      new CopyObjectCommand({
        Bucket: bucket,
        Key: destinationKey,
        CopySource: this.copySourceFor(bucket, sourceKey),
        ContentType: head.ContentType,
      }),
    );
  }

  /**
   * `CopySource` must be URI-encoded: keys legitimately contain characters the
   * parser would otherwise read as part of the bucket/key separator or as query
   * syntax.
   *
   * Takes `bucket` explicitly rather than reading `this.bucket`, so a caller
   * mid-copy always encodes the source against the bucket it started with.
   */
  private copySourceFor(bucket: string, sourceKey: string): string {
    return encodeURIComponent(`${bucket}/${sourceKey}`);
  }

  /**
   * Composes a copy larger than 5 GiB out of ranged `UploadPartCopy` calls.
   *
   * Not decorated: it is only ever reached from `copyObject`, which has already
   * ensured the credentials are valid.
   *
   * The `catch` is load-bearing rather than tidy. An incomplete multipart upload
   * keeps its uploaded parts in the bucket, billable and invisible in a normal
   * object listing, so a failure that skipped the abort would leave the tenant
   * paying for storage nobody can see. The abort is itself best-effort — if it
   * also fails there is nothing further to try from here, and the original error
   * is the one worth surfacing.
   */
  private async copyObjectMultipart(
    bucket: string,
    sourceKey: string,
    destinationKey: string,
    size: number,
    contentType?: string,
  ): Promise<void> {
    const created = await this.s3Client!.send(
      new CreateMultipartUploadCommand({
        Bucket: bucket,
        Key: destinationKey,
        ContentType: contentType,
      }),
    );

    const uploadId = created.UploadId;
    if (!uploadId) {
      throw new Error(
        'Failed to start a multipart copy: no UploadId returned.',
      );
    }

    try {
      const ranges = copyPartRanges(size);
      const copied = await mapWithConcurrency(
        ranges,
        COPY_CONCURRENCY,
        async (part) => {
          const result = await this.s3Client!.send(
            new UploadPartCopyCommand({
              Bucket: bucket,
              Key: destinationKey,
              UploadId: uploadId,
              PartNumber: part.partNumber,
              CopySource: this.copySourceFor(bucket, sourceKey),
              CopySourceRange: `bytes=${part.start}-${part.end}`,
            }),
          );

          const etag = result.CopyPartResult?.ETag;
          if (!etag) {
            throw new Error(`Part ${part.partNumber} copied without an ETag.`);
          }
          return { PartNumber: part.partNumber, ETag: etag };
        },
      );

      await this.s3Client!.send(
        new CompleteMultipartUploadCommand({
          Bucket: bucket,
          Key: destinationKey,
          UploadId: uploadId,
          // S3 requires parts in ascending order, which concurrent completion
          // does not preserve.
          MultipartUpload: {
            Parts: copied.sort((a, b) => a.PartNumber - b.PartNumber),
          },
        }),
      );
    } catch (error) {
      try {
        await this.s3Client!.send(
          new AbortMultipartUploadCommand({
            Bucket: bucket,
            Key: destinationKey,
            UploadId: uploadId,
          }),
        );
      } catch (abortError) {
        console.error(
          `Could not clean up an incomplete copy.${detail(
            `Aborting the multipart copy of "${sourceKey}" failed; its parts may still be billable.`,
          )}`,
          ...(LOG_DETAIL ? [abortError] : []),
        );
      }
      throw error;
    }
  }

  /**
   * A short-lived URL the browser can save `key` from directly, as `fileName`.
   * The bytes never pass through JavaScript, so this works for files too big
   * to hold in memory. Kept short because anyone holding the URL can use it.
   */
  @requiresCredentials
  public async getDownloadUrl(
    key: string,
    fileName: string,
    expiresIn = 60,
  ): Promise<string> {
    return getSignedUrl(
      this.s3Client!,
      new GetObjectCommand({
        Bucket: this.bucket,
        Key: key,
        // Cross-origin, a link's `download` attribute is ignored; this header
        // is what makes the browser save the file rather than open it.
        ResponseContentDisposition: `attachment; filename*=UTF-8''${encodeURIComponent(fileName)}`,
      }),
      { expiresIn },
    );
  }

  /**
   * Downloads a file from S3 in chunks, yielding progress updates and the final blob.
   * @param fileName The key of the file to download.
   */
  @requiresCredentialsStream
  public async *downloadFileWithProgress(fileName: string): AsyncGenerator<{
    total: number;
    downloaded: number;
    progress: number;
    blob?: Blob;
  }> {
    // Captured once: this generator can straddle several credential
    // refreshes across a long download, and every chunk must come from the
    // bucket the initial HeadObject did.
    const bucket = this.bucket;
    const headCommand = new HeadObjectCommand({
      Bucket: bucket,
      Key: fileName,
    });
    const headResponse = await this.s3Client!.send(headCommand);
    const totalBytes = headResponse.ContentLength ?? 0;
    yield { total: totalBytes, downloaded: 0, progress: 0 };

    const chunkSize = 1024 * 1024; // 1MB
    const chunks: Uint8Array<ArrayBufferLike>[] = [];
    let downloadedBytes = 0;

    for (let start = 0; start < totalBytes; start += chunkSize) {
      const end = Math.min(start + chunkSize - 1, totalBytes - 1);

      const getCommand = new GetObjectCommand({
        Bucket: bucket,
        Key: fileName,
        Range: `bytes=${start}-${end}`,
      });

      const response = await this.s3Client!.send(getCommand);

      if (
        !response.Body ||
        typeof response.Body.transformToByteArray !== 'function'
      ) {
        throw new Error(
          'Response body does not support transformToByteArray()',
        );
      }

      const chunk = await response.Body.transformToByteArray();

      chunks.push(chunk);
      downloadedBytes += chunk.length;

      const progress = Math.round((downloadedBytes / totalBytes) * 100);
      yield { total: totalBytes, downloaded: downloadedBytes, progress };
    }

    const blob = new Blob(chunks.map((c) => new Uint8Array(c)));

    yield {
      total: totalBytes,
      downloaded: downloadedBytes,
      progress: 100,
      blob,
    };
  }
}

/**
 * Initializes the singleton S3VDRClient instance.
 *
 * @param store The Ember Data store, used to reach the application adapter.
 */
export async function initializeVdrClient(
  store: StoreService,
): Promise<S3VDRClient> {
  if (s3VdrClient) {
    s3VdrClient.updateStore(store);
    return s3VdrClient;
  }
  // Assigned *before* the first `await`, and that ordering is load-bearing.
  // A 401 from the credentials endpoint sends the adapter into
  // `session.refreshToken()`, which re-authenticates, which fires
  // `handleAuthentication()`, which calls this function again. The early return
  // on a non-null singleton is what stops that cycle — and it can only see a
  // non-null singleton if the assignment happens before we yield.
  s3VdrClient = new S3VDRClient({ store });
  await s3VdrClient.initializeClient();
  return s3VdrClient;
}

/**
 * Drops the singleton. Tests only: the module outlives an individual QUnit
 * container, so without this one test's destroyed store leaks into the next.
 */
export function resetVdrClient(): void {
  s3VdrClient = null;
}

export default initializeVdrClient;
